import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label
} from 'recharts';
import { ETHNICITY_COLORS, QUADRANT_THRESHOLDS, formatPercentage } from '../../../../lib/utils/deepDiveUtils';
import QuadrantDetailTable from './QuadrantDetailTable';

const TheaterPaymentQuadrant = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No quadrant data available
        </Typography>
      </Paper>
    );
  }

  // Transform data for scatter plot
  const scatterData = data.map(record => ({
    x: record.PctTheater || 0,
    y: record.PctWillingToPay || 0,
    size: record.totalResponses || 10,
    ethnicity: record.ethnicity || 'Unknown',
    agegroup: record.agegroup || 'Unknown',
    gender: record.gender || 'Unknown',
    responses: record.totalResponses || 0,
    movieFrequency: record.movie_frequency_category || 'Unknown'
  }));

  // Calculate quadrant distributions
  const quadrants = {
    'Ideal Theatrical': { data: [], total: 0 },
    'Streaming Opportunity': { data: [], total: 0 },
    'Promotional Target': { data: [], total: 0 },
    'Marketing Challenge': { data: [], total: 0 }
  };

  scatterData.forEach(point => {
    const theater = point.x >= QUADRANT_THRESHOLDS.theater;
    const payment = point.y >= QUADRANT_THRESHOLDS.payment;
    
    let quadrant;
    if (theater && payment) quadrant = 'Ideal Theatrical';
    else if (!theater && payment) quadrant = 'Streaming Opportunity';
    else if (theater && !payment) quadrant = 'Promotional Target';
    else quadrant = 'Marketing Challenge';

    quadrants[quadrant].data.push(point);
    quadrants[quadrant].total += point.responses;
  });

  const totalResponses = Object.values(quadrants).reduce((sum, q) => sum + q.total, 0);

  // Quadrant metadata
  const quadrantInfo = [
    {
      name: 'Ideal Theatrical',
      color: '#4caf50',
      description: 'Theater + Willing to Pay',
      position: 'Top Right',
      strategy: 'Premium theatrical push'
    },
    {
      name: 'Streaming Opportunity',
      color: '#ffc107',
      description: 'Willing to Pay but NOT Theater',
      position: 'Top Left',
      strategy: 'PVOD/Streaming focus'
    },
    {
      name: 'Promotional Target',
      color: '#ff9800',
      description: 'Theater but NOT Willing to Pay',
      position: 'Bottom Right',
      strategy: 'Discount/promo offers'
    },
    {
      name: 'Marketing Challenge',
      color: '#f44336',
      description: 'Neither Theater nor Payment',
      position: 'Bottom Left',
      strategy: 'Value proposition work'
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {data.ethnicity} - {data.agegroup} {data.gender}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Theater Intent: <strong>{formatPercentage(data.x)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Willing to Pay: <strong>{formatPercentage(data.y)}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {data.movieFrequency}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Theater vs Payment Intent Quadrant Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Scatter Plot */}
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Theater Intent"
                    domain={[0, 100]}
                    label={{
                      value: 'Theater Intent %',
                      position: 'bottom',
                      offset: 40,
                      style: { fontWeight: 600, fontSize: '0.9rem' }
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="Willing to Pay"
                    domain={[0, 100]}
                    label={{
                      value: 'Willing to Pay %',
                      angle: -90,
                      position: 'left',
                      offset: 40,
                      style: { fontWeight: 600, fontSize: '0.9rem' }
                    }}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  
                  {/* Reference lines for quadrants */}
                  <ReferenceLine
                    x={QUADRANT_THRESHOLDS.theater}
                    stroke="#999"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                  <ReferenceLine
                    y={QUADRANT_THRESHOLDS.payment}
                    stroke="#999"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />

                  {/* Quadrant Labels - Positioned correctly in each quadrant */}
                  {/* Top Right: Ideal Theatrical (theater AND payment) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.theater + 25} strokeWidth={0}>
                    <Label 
                      value="IDEAL THEATRICAL" 
                      position={{x: '50%', y: 75}} 
                      fill="#4caf50" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Top Left: Streaming Opportunity (NOT theater but payment) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.theater - 25} strokeWidth={0}>
                    <Label 
                      value="STREAMING OPP" 
                      position={{x: '50%', y: 75}} 
                      fill="#ffc107" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Bottom Right: Promotional Target (theater but NOT payment) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.theater + 25} strokeWidth={0}>
                    <Label 
                      value="PROMO TARGET" 
                      position={{x: '50%', y: 25}} 
                      fill="#ff9800" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>
                  
                  {/* Bottom Left: Marketing Challenge (neither theater nor payment) */}
                  <ReferenceLine x={QUADRANT_THRESHOLDS.theater - 25} strokeWidth={0}>
                    <Label 
                      value="CHALLENGE" 
                      position={{x: '50%', y: 25}} 
                      fill="#f44336" 
                      fontSize={12} 
                      fontWeight={700}
                    />
                  </ReferenceLine>

                  <Scatter data={scatterData}>
                    {scatterData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={ETHNICITY_COLORS[entry.ethnicity] || '#757575'}
                        opacity={0.7}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              {/* Legend */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                {Object.keys(ETHNICITY_COLORS).map(ethnicity => (
                  <Box key={ethnicity} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: ETHNICITY_COLORS[ethnicity]
                      }}
                    />
                    <Typography variant="caption">{ethnicity}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quadrant Summary Cards */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {quadrantInfo.map(quadrant => {
              const quadrantData = quadrants[quadrant.name];
              const percentage = totalResponses > 0
                ? Math.round((quadrantData.total / totalResponses) * 100)
                : 0;

              return (
                <Grid item xs={12} sm={6} lg={12} key={quadrant.name}>
                  <Card
                    sx={{
                      border: `2px solid ${quadrant.color}`,
                      backgroundColor: quadrant.color + '05'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: quadrant.color,
                            borderRadius: '50%'
                          }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {quadrant.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {quadrant.description}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: quadrant.color }}>
                        {percentage}%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          p: 0.5,
                          backgroundColor: quadrant.color + '15',
                          borderRadius: 1,
                          fontStyle: 'italic'
                        }}
                      >
                        Strategy: {quadrant.strategy}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Detailed Breakdown Tables */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Detailed Demographic Breakdown by Quadrant
          </Typography>
          <Grid container spacing={2}>
            {quadrantInfo.map(quadrant => (
              <Grid item xs={12} md={6} key={quadrant.name}>
                <QuadrantDetailTable
                  quadrantName={quadrant.name}
                  quadrantColor={quadrant.color}
                  data={quadrants[quadrant.name].data}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TheaterPaymentQuadrant;

